import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { collection, query, where, getDocs, updateDoc, doc } from "firebase/firestore";
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  onAuthStateChanged,
  sendPasswordResetEmail 
} from "firebase/auth";
import { db, auth } from "../firebase";

const LoginForm = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // SECURE: Automatically direct logged-in users away from the login page
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) navigate("/admin", { replace: true });
    });
    return () => unsubscribe();
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    toast.dismiss(); 
    setLoading(true);

    try {
      // 1. Try native Firebase Auth first (Success path for post-migrated logins)
      await signInWithEmailAndPassword(auth, email, password);
      toast.success("Welcome back! Login successful.");
      setLoading(false);
      return; 
    } catch (authError) {
      console.log("authentication failed. Attempting silent legacy fallback...");
    }

    try {
      // 2. Query the legacy collection for a matching record
      const adminsRef = collection(db, "admins");
      const q = query(adminsRef, where("username", "==", email), where("password", "==", password));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const legacyDoc = querySnapshot.docs[0];
        toast.info("Upgrading account security settings...");

        try {
          // 3. SECURE MIGRATION: Auto-register user into Firebase Auth
          await createUserWithEmailAndPassword(auth, email, password);

          // 4. CLEANUP: Only strip plaintext password if Auth registration succeeds!
          const adminDocRef = doc(db, "admins", legacyDoc.id);
          await updateDoc(adminDocRef, {
            password: "MIGRATED_TO_FIRE_AUTH" 
          });

          toast.success("Security configuration upgraded successfully! Welcome.");
        } catch (migrationError) {
          console.error("Migration step failed:", migrationError);
          
          if (migrationError.code === "auth/email-already-in-use") {
            // User exists in Auth, but typed an outdated Firestore password!
            toast.error("Incorrect password. If you recently reset your password, please use your newest one.");
          } else if (migrationError.code === "auth/weak-password") {
            // Catches passwords under 6 characters
            toast.error("Migration failed: Passwords must be at least 6 characters long.");
          } else {
            toast.error("Failed to upgrade modern security profile.");
          }
        }
      } else {
        toast.error("Unauthorized credentials entered.");
      }
    } catch (err) {
      console.error("System authentication error:", err);
      toast.error("A system error occurred during authentication.");
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Forgot password handler
  const handleForgotPassword = async () => {
    if (!email) {
      toast.error("Please enter your email address in the field above first.");
      return;
    }

    toast.dismiss();
    setLoading(true);

    try {
      await sendPasswordResetEmail(auth, email);
      // Clean universal text since Firebase protects user enumeration by default
      toast.success("If an account exists, a password reset link has been sent to your inbox!");
    } catch (err) {
      console.error("Error sending reset email:", err);
      toast.error("Could not send reset email. Please try again.");
    } finally {
      setLoading(false);
    }
  };


  return (
    <div
      className="Login-section contact-section padding-container"
      style={{ background: "white", position: "relative" }}
    >
      <div className="page-wrapper">
        <div className="contact-bg">
          <div className="contact-us-form">
            <div className="contact-text">
              <h1>LOGIN</h1>
              <p>Login to access your admin account</p>
            </div>

            <div className="contact-form">
              <h1>Login</h1>
              <form className="form-data" onSubmit={handleLogin}>
                <div className="form-username">
                  <label htmlFor="username">Email:</label>
                  <input
                    type="email"
                    name="username"
                    id="username"
                    placeholder="Email Address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    autoComplete="off" 
                    required
                  />
                </div>

                <div className="form-phone password">
                  <label htmlFor="password">Password</label>
                  <input
                    type="password"
                    name="password"
                    id="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    autoComplete="off" 
                    required
                  />
                </div>

                {/* 🔹 Forgot Password Link Button */}
                <div style={{ textAlign: "right", margin: "10px 0" }}>
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    disabled={loading}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#007bff",
                      cursor: "pointer",
                      textDecoration: "underline",
                      fontSize: "14px",
                      padding: 0
                    }}
                  >
                    Forgot Password?
                  </button>
                </div>

                <button type="submit" className="form-submit" disabled={loading}>
                  {loading ? "Authenticating..." : "Login"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;